/**
 * Connect Section - Join the Network
 * ===================================
 * Social media links with simple CSS-based effects
 * No canvas particles - uses CSS animations instead
 */

import React, { useState, useEffect, useRef } from 'react';
import { config } from '../../data/config';
import './Connect.css';

const socialLinks = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'fab fa-whatsapp',
    url: config.social.whatsapp,
    color: '#25D366',
    delay: 0
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: 'fab fa-telegram-plane',
    url: config.social.telegram,
    color: '#0088cc',
    delay: 0.1
  },
  {
    id: 'twitter',
    name: 'Twitter',
    icon: 'fab fa-twitter',
    url: config.social.twitter,
    color: '#1DA1F2',
    delay: 0.2
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'fab fa-facebook-f',
    url: config.social.facebook,
    color: '#4267B2',
    delay: 0.3
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: 'fab fa-instagram',
    url: config.social.instagram,
    color: '#E4405F',
    delay: 0.4
  },
  {
    id: 'youtube',
    name: 'YouTube',
    icon: 'fab fa-youtube',
    url: config.social.youtube,
    color: '#FF0000',
    delay: 0.5
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'fab fa-linkedin-in',
    url: config.social.linkedin,
    color: '#0A66C2',
    delay: 0.6
  }
];

// Social Link Component
const SocialLink = ({ social, isInView }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setIsVisible(true), 300 + social.delay * 1000);
      return () => clearTimeout(timer);
    }
  }, [isInView, social.delay]);

  return (
    <a
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`social-orb ${isVisible ? 'in-view' : ''}`}
      style={{ '--social-color': social.color }}
    >
      {/* Outer Ring */}
      <div className="social-ring" />
      
      {/* Icon */}
      <div className="social-icon">
        <i className={social.icon} />
      </div>

      {/* Label */}
      <span className="social-label">{social.name}</span>
    </a>
  );
};

const Connect = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="connect-section" ref={sectionRef} id="connect">
      {/* Background with CSS particles */}
      <div className="connect-bg">
        {/* Subtle glows */}
        <div className="connect-glow connect-glow-1" />
        <div className="connect-glow connect-glow-2" />
        
        {/* CSS-only floating dots - 25 bigger particles */}
        <div className="simple-particles">
          {[...Array(25)].map((_, i) => (
            <span 
              key={i} 
              className="particle-dot"
              style={{
                '--x': `${Math.random() * 100}%`,
                '--y': `${Math.random() * 100}%`,
                '--duration': `${12 + Math.random() * 15}s`,
                '--delay': `${Math.random() * 5}s`,
                '--size': `${4 + Math.random() * 6}px`
              }}
            />
          ))}
        </div>
        <div className="connect-vignette" />
      </div>

      <div className="connect-container">
        {/* Header */}
        <div className={`connect-header ${isInView ? 'in-view' : ''}`}>
          <div className="section-badge">
            <i className="fas fa-globe" />
            <span>Join the Network</span>
          </div>
          <h2 className="section-title">
            Let's <span className="text-highlight">Connect</span>
          </h2>
          <p className="section-subtitle">
            Follow us on social media and stay updated with the latest in Web3
          </p>
        </div>

        {/* Social Links Grid */}
        <div className="social-grid">
          {socialLinks.map((social) => (
            <SocialLink
              key={social.id}
              social={social}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Tagline */}
        <div className={`connect-tagline ${isInView ? 'in-view' : ''}`}>
          <p>Building the future, one block at a time.</p>
        </div>
      </div>
    </section>
  );
};

export default Connect;
